import { AUTH_URL } from "../constants"
import { AUTH_KEY } from "../context/authContext";
import { APIRequest, HTTPClient, RequestType } from "../services/http_client"
import LocalStorageService from "../services/localstorage.service";
import { MagazineType } from "../types/magazine";

const client = new HTTPClient();

export interface MagazinesFilterType {
    date: string;
    category: number;
    published: number;
    limit: number;
    page: number;
}

interface MagazineResponse {
    data: Array<MagazineType>;
    total: number;
}

export interface ResponseType {
    newspaper?: MagazineResponse;
    error?: string;
}

export interface MagazineCategoriesResponse {
    magazine_categories?: Array<{id: number, name: string}>;
    error?: string;
}

export interface MagazineIdResponse {
    magazine?: MagazineType;
    error?: string;
}

interface CRUDResponse {
    success: boolean;
    error: any;
}

interface AdobeResponse {
    status: string;
    errors: string[];
    deleted: boolean;
}

interface DeleteAttachmentParams {
    magazineId: number;
    fileId: number;
}

interface DeleteMagazine {
    magazineId: number;
}

class MagazinesModel {

    static async triggerMagazineDownload(): Promise<AdobeResponse> {
        const auth = LocalStorageService.get(AUTH_KEY)
        if(!auth) return {
            status: 'error',
            errors: ['User not authorized'],
            deleted: false,
        }

        const request = new APIRequest(
            RequestType.GET,
            `${AUTH_URL}/newspaper/download/adobe/zip`,
            undefined,
            auth.accessToken,
        )

        const response = await client.get(request);
        if (!response.success) {
            return {
                status: 'error',
                errors: [response.errorMessage ?? ''],
                deleted: false,
            }
        }
        return response.result  as unknown as AdobeResponse
    }

    static async deleteMagazine({ magazineId } : DeleteMagazine) {
        if(!magazineId) return

        const auth = LocalStorageService.get(AUTH_KEY)
        if(!auth) return {
            error: "Unauthorized"
        }

        const request = new APIRequest(
            RequestType.DELETE,
            `${AUTH_URL}/newspaper/${magazineId}`,
            undefined,
            auth.accessToken,
        );

        const response = await client.delete(request);

        if (!response.success) {
            return {
                error: response.errorMessage,
            }
        }
  
        return response.result as unknown as CRUDResponse
    }

    static async deleteAttachment({ magazineId, fileId } : DeleteAttachmentParams) {
        if(!magazineId || !fileId) return

        const auth = LocalStorageService.get(AUTH_KEY)
        if(!auth) return {
            error: "Unauthorized"
        }

        const request = new APIRequest(
            RequestType.DELETE,
            `${AUTH_URL}/newspaper/attachment/${magazineId}/${fileId}`,
            undefined,
            auth.accessToken,
        );

        const response = await client.delete(request);

        if (!response.success) {
            return {
                error: response.errorMessage,
            }
        }
  
        return response.result as unknown as CRUDResponse
    }

    static async updateMagazine(data: any){
        const auth = LocalStorageService.get(AUTH_KEY)
        if(!auth) return {
            error: "Unauthorized"
        }

        const request = new APIRequest(
            RequestType.PUT,
            `${AUTH_URL}/newspaper/update/${data.id}`,
            JSON.stringify(data),
            auth.accessToken,
            {},
            900000,
        );

        const response = await client.put(request);

        if (!response.success) {
            return {
                error: [
                    response.errorMessage ?? '',
                    response.result.error ?? '',
                ].join('\n'),
            }
        }
  
        return response.result as unknown as CRUDResponse
    }

    static async createMagazine(data: any){
        const auth = LocalStorageService.get(AUTH_KEY)
        if(!auth) return {
            error: "Unauthorized"
        }

        const request = new APIRequest(
            RequestType.POST,
            `${AUTH_URL}/newspaper/create`,
            JSON.stringify(data),
            auth.accessToken,
            {},
            900000,
        );

        const response = await client.post(request);

        if (!response.success) {
            return  {
                error: [
                    response.errorMessage ?? '',
                    response.result.error ?? '',
                ].join('\n'),
            }
        }
  
        return response.result as unknown as CRUDResponse
    }

    static async getMagazine(id: number) {
        const auth = LocalStorageService.get(AUTH_KEY)
        if(!auth) return {
            error: "Unauthorized"
        }

        const request = new APIRequest(
            RequestType.GET,
            `${AUTH_URL}/newspaper/${id}`,
            undefined,
            auth.accessToken,
        );

        const response = await client.get(request);

        if (!response.success) {
            return {
                error: response.errorMessage,
            }
        }
  
        return response.result as unknown as MagazineIdResponse
    }

    static async getAll(filters : MagazinesFilterType){
        const auth = LocalStorageService.get(AUTH_KEY)
        if(!auth) return {
            error: "Unauthorized"
        }

        const request = new APIRequest(
            RequestType.POST,
            `${AUTH_URL}/newspaper`,
            JSON.stringify(filters),
            auth.accessToken,
        );

        const response = await client.post(request);

        if (!response.success) {
            return {
                error: response.errorMessage,
            }
        }
  
        return response.result as unknown as ResponseType
    }

    static async getCategories(){
        const auth = LocalStorageService.get(AUTH_KEY)
        if(!auth) return {
            error: "Unauthorized"
        }

        const request = new APIRequest(
            RequestType.GET,
            `${AUTH_URL}/newspaper/categories`,
            undefined,
            auth.accessToken,
        );

        const response = await client.get(request);

        if (!response.success) {
            return {
                error: response.errorMessage,
            }
        }
  
        return response.result as unknown as MagazineCategoriesResponse
    }
}

export default MagazinesModel